import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type WorkflowTransitionSession = {
  startedActions: Readonly<Record<string, string>>;
  markStarted: (workflowInstanceName: string, action: string) => void;
  clearStarted: (workflowInstanceName: string) => void;
};

const WorkflowTransitionSessionContext = createContext<
  WorkflowTransitionSession | undefined
>(undefined);

export const WorkflowTransitionSessionProvider = ({
  children,
}: PropsWithChildren) => {
  const [startedActions, setStartedActions] = useState<Record<string, string>>(
    {},
  );

  const markStarted = useCallback(
    (workflowInstanceName: string, action: string) =>
      setStartedActions(current => ({
        ...current,
        [workflowInstanceName]: action,
      })),
    [],
  );
  const clearStarted = useCallback(
    (workflowInstanceName: string) =>
      setStartedActions(
        ({ [workflowInstanceName]: _cleared, ...remaining }) => remaining,
      ),
    [],
  );

  const value = useMemo(
    () => ({ startedActions, markStarted, clearStarted }),
    [startedActions, markStarted, clearStarted],
  );

  return (
    <WorkflowTransitionSessionContext.Provider value={value}>
      {children}
    </WorkflowTransitionSessionContext.Provider>
  );
};

export const useWorkflowTransitionSession = (): WorkflowTransitionSession => {
  const session = useContext(WorkflowTransitionSessionContext);
  if (!session) {
    throw new Error(
      'useWorkflowTransitionSession must be used within a WorkflowTransitionSessionProvider',
    );
  }
  return session;
};
